import type { StageResult, ValidationIssue, BlockBundle } from "../types"
import { scanExpressionPrefixes } from "./stage-actionflow"
import { KNOWN_EXPRESSION_ROOTS, LEGACY_EXPR_PREFIXES } from "../vocab/expression-namespaces"
import { getElementField } from "../utils/element-props"
// AUTO-GENERATED (scripts/generate-expression-accessor-globals-json.ts) from
// cogo-kg-browser/fixtures/expression-accessors-v1.ttl — GlobalFn + DollarFn
// accessor names only. See `DECLARED_GLOBAL_FUNCTIONS` below for why this is
// a checked-in JSON snapshot rather than a live TTL read.
import expressionAccessorGlobals from "../../ontology/expression-accessor-globals.v1.json" with { type: "json" }

// Roots that are legal at the head of an expression — always valid, no
// declaration needed. Copy #2 of 5 (pre-fold) was a hand-typed list here; it is
// now the one composed vocabulary (src/vocab/expression-namespaces.ts).
const RUNTIME_NAMESPACES = KNOWN_EXPRESSION_ROOTS

// Valid method names in Cogo expression evaluator.
// Source-of-truth: cogo-web/packages/cogo-kg-browser/fixtures/expression-accessors-v1.ttl
// (Wave 11D Q-D5). Entries below mirror TTL categories that match the
// `/\.([a-zA-Z_]\w*)\s*\(/` regex — i.e. `.method()` patterns only.
// GlobalFn (today/monthOffset/formatCurrency/...) and DollarFn ($map/$sum/...)
// are top-level / $-prefixed, so the `.method(` regex never matches them —
// they are call-position bare identifiers instead, handled separately by
// `extractCalleesFromExpr` / `DECLARED_GLOBAL_FUNCTIONS` / the
// `expr-unknown-global-fn` rule below (that exclusion used to be silent —
// closed as of the callee-vs-path fix, see those definitions for why).
//
// Wave 11D.b (2026-04-24): pruned 37 over-claimed silent-fail methods.
// Surfaced 6 stealth-bug corpus blocks (now emitting expr-invalid-method warnings).
//
// Wave 11D.c (2026-04-25): re-added pow / every / some as canonical after
// implementing the matching runtime handlers in compute-evaluator.ts. Remaining
// corpus gaps outside #{…}-only scanner coverage deferred (form/search-filters
// Object.keys, form/question-wizard IIFE indexOf).
//
// ── `Object.keys` (form/search-filters): an OPEN either/or, deliberately not
//    decided here (2026-08-26) ────────────────────────────────────────────────
// The one remaining `expr-invalid-method` error in the corpus is
// `#{Object.keys(state.amenities).filter(k => state.amenities[k]).join(',')}`
// at `sources.searchApi.params.amenities`. The other two carriers
// (`crypto.randomUUID`, `URL.createObjectURL` in form/file-upload) were corpus
// defects and are gone; this one is NOT, and admitting an accessor for it
// widens what every generated block may emit, so it is written down rather
// than taken unilaterally. Measured against the real evaluator
// (`@cogo/runtime-v2` compute-evaluator, the sole canonical runtime):
//
//   Today          → the expression yields `[]`, not the joined string the
//                    datasource param wants, and NOT because of the legacy
//                    `state.` prefix: with `local.` it is still `[]`.
//   Admit          → `Object.keys` (or a `$keys` DollarFn, which matches the
//                    project's own naming for object/array helpers and, unlike
//                    `Object.*`, needs no namespace concept in the grammar).
//                    Cost: the evaluator must implement key enumeration; every
//                    generated block may then emit it. The demonstrated NEED is
//                    weak — `ui.cogo.json` hard-codes all four amenity keys in
//                    four separate bindings, so nothing here is dynamic.
//   Reshape        → hold the selected keys as a string array; the param then
//                    becomes `#{local.selectedAmenities.join(',')}`, which was
//                    measured returning "ac,garden". This is the only
//                    in-vocabulary route that produces the right VALUE, and it
//                    is blocked today by TWO consumer defects, neither of them
//                    in the accessor set:
//                      (a) `executeConditional` stringifies the condition
//                          through `resolveValue` before `compute.evaluate`
//                          sees it, so `#{local.sel.includes(event.params.x)}`
//                          is always falsy — a toggle can only ever append.
//                          (Bare `.includes()` in the evaluator itself returns
//                          a correct boolean; the engine is what loses it.)
//                      (b) `StateManager.appendTo` is `[...arr, value]`, so
//                          `operation:"append"` with an ARRAY value nests it as
//                          one element — which is what 4 of the 5 existing
//                          corpus `append` call sites do today.
//   Nothing else   → array literals are not evaluated at all
//                    (`#{['ac','x'].join(',')}` echoes its source), chained
//                    `.filter().map().join()` over objects returns null, and
//                    `concat()` splits its args on commas inside ternaries.
//
// Recommendation: fix (a)+(b) in cogo-runtime-v2 — they are contained, they
// unblock this block via Reshape, and (b) is already silently wrong for four
// other blocks — rather than widen the accessor surface for a static
// four-key enumeration.
export const VALID_EXPRESSION_METHODS: ReadonlySet<string> = new Set([
  // String (10 — AccessorString)
  "toUpperCase", "toLowerCase", "trim",
  "includes", "startsWith", "endsWith",
  "replace", "substring", "split", "toString",
  // Number (1 new — AccessorNumber; toString overlaps String)
  "toFixed",
  // Array (11 — AccessorArrayPure + AccessorArrayChained; length overlaps String)
  "map", "filter", "find", "reduce", "sort", "slice", "join", "length", "unique",
  "every", "some", // Wave 11D.c — boolean predicate handlers
  // Math (7 — MathMethod; accessed via Math.xxx → regex captures .method portion)
  "max", "min", "round", "floor", "ceil", "abs",
  "pow", // Wave 11D.c — exponent handler
])

/**
 * Global-function accessor names — the counterpart to `VALID_EXPRESSION_METHODS`
 * for accessors called WITHOUT a leading `.` (`concat(...)`, `$sum(...)`).
 * Same source-of-truth (`expression-accessors-v1.ttl`), filtered to
 * `cogo:accessorCategory` `"GlobalFn"` or `"DollarFn"`.
 *
 * DERIVED, never typed here: `expression-accessor-globals.v1.json` is
 * generated from the TTL by `scripts/generate-expression-accessor-globals-
 * json.ts` and held byte-identical to a fresh TTL parse by
 * `src/vocab/__tests__/expression-accessor-globals-derivation.test.ts`.
 *
 * Not a live TTL read: `stage-runtime-expr.ts` sits on `@cogo/validator/
 * browser`'s import graph (`jsonb-api.ts` → `runRuntimeExprStage`), and
 * `apps/cogo-studio`'s production `vite.config.ts` does not register the
 * `*.ttl` text-loader plugin that only its `vitest.config.ts` carries (for
 * an unrelated, non-browser-path consumer — `stage-cobo-backoffice.ts` via
 * `@cogo/kg-repository/decisions`). A direct `import ttl from "*.ttl"` here
 * would pass this package's own tests and then break the real Vite bundle.
 * A static JSON import is the same browser-safe mechanism
 * `expression-namespaces.ts` already uses for `expression-prefix.json` and
 * `ontology-loader.ts` uses for the other 7 bundled ontology files.
 */
export const DECLARED_GLOBAL_FUNCTIONS: ReadonlySet<string> = new Set(
  (expressionAccessorGlobals as { names: string[] }).names,
)

/**
 * Identifiers immediately followed by `(` — callees, not state paths.
 * Structural discriminator, no vocabulary needed: any bare identifier in
 * call position is a callee, whether it turns out to be a declared global
 * function (`concat`, `$sum`) or a typo. `.method(` is a different concern,
 * excluded by requiring the character before the identifier to NOT be `.`
 * (or word/`$`) — that shape is already validated against
 * `VALID_EXPRESSION_METHODS` by `validateMethods` above.
 *
 * String literals are stripped first, the same way `extractPathsFromExpr`
 * strips them before tokenizing. Without this, a corpus probe (283 fixture
 * files under this package + 237 under gridone/wonbanking + a spot-check of
 * cogo-web) found real false positives: a CSS value like
 * `'hsl(var(--primary) / 0.05)'` or plain prose like `'Switch to Annual
 * (Save 20%)'` sitting inside a ternary's string branch reads, on the raw
 * body, as call-position identifiers `hsl`/`var`/`Annual` — none of which
 * are expressions at all. (Backtick template literals are NOT stripped here
 * — a pre-existing gap `extractPathsFromExpr` shares, not introduced or
 * worsened by this function; left alone, see fix-round-1 report.)
 *
 * Lookaround, not a consuming boundary match: an earlier version used
 * `(^|[^\w$.])([A-Za-z_$][\w$]*)\s*\(` — that alternative CONSUMES the
 * boundary character (including the trailing `\(` itself) as part of the
 * overall match. For a nested call whose identifier sits immediately after
 * the outer call's `(` — `concat(formatCurrency(...))` — the outer match
 * "concat(" eats the very `(` the inner identifier needed as its own
 * boundary, so `formatCurrency` is never found: reintroducing this task's
 * own bug for composed calls, AND silently swallowing a nested typo
 * (`$sum($mapz(...))` produces zero issues from either side, since
 * `extractPathsFromExpr` also excludes `$`-prefixed tokens). Fix-round-1
 * (reviewer-caught). `(?<![\w$.])`/`(?=\s*\()` are zero-width — a match
 * consumes only the identifier itself, so back-to-back calls with no
 * separating whitespace are each found independently, at any nesting depth.
 */
function extractCalleesFromExpr(body: string): Set<string> {
  const withoutStringLiterals = body.replace(/["'][^"']*["']/g, "")
  const callees = new Set<string>()
  const pattern = /(?<![\w$.])([A-Za-z_$][\w$]*)(?=\s*\()/g
  let m: RegExpExecArray | null
  while ((m = pattern.exec(withoutStringLiterals)) !== null) callees.add(m[1])
  return callees
}

// Arrow function callback patterns: (sum, item) => ..., (item) => ..., item => ...
// Uses (\w[\w\s,]*) to match only identifier-like params inside the closest parens before =>
const ARROW_CALLBACK_PATTERN = /\((\w[\w\s,]*)\)\s*=>|(\w+)\s*=>/g

// JS reserved words that can sit immediately before `(` in valid expression
// syntax without being a function call: `typeof(x)` (the parenthesised
// operand of the `typeof` operator) and an IIFE's `function(){...}()` head.
// `extractCalleesFromExpr` is intentionally vocabulary-free (any identifier
// in call position is a callee, full stop) — this is JS grammar, not Cogo
// domain vocabulary, so it lives as the exemption `validateGlobalFunctionCalls`
// applies, not a change to what counts as a callee. Not observed in the
// corpus today; fix-round-1 Minor.
const CALL_LIKE_JS_KEYWORDS: ReadonlySet<string> = new Set(["typeof", "function"])

interface ExprLocation {
  file: string
  path: string
  raw: string      // the full string containing the expression
  body: string     // content inside #{ }
  insideIterateOver: boolean
}

// ── Wave 9A — UI expression prefix scanner ───────────────────────────────────

function walkUiStrings(
  node: Record<string, unknown> | undefined,
  path: string,
  visit: (value: string, p: string) => void,
): void {
  if (!node || typeof node !== "object") return
  for (const [k, v] of Object.entries(node)) {
    const p = `${path}.${k}`
    if (typeof v === "string") visit(v, p)
    else if (Array.isArray(v)) {
      v.forEach((item, i) => {
        if (typeof item === "string") visit(item, `${p}[${i}]`)
        else if (item && typeof item === "object") walkUiStrings(item as Record<string, unknown>, `${p}[${i}]`, visit)
      })
    } else if (v && typeof v === "object") {
      walkUiStrings(v as Record<string, unknown>, p, visit)
    }
  }
}

export function analyzeUiExpressionPrefixes(
  issues: ValidationIssue[],
  uiRoot: Record<string, unknown> | undefined,
): void {
  if (!uiRoot) return
  walkUiStrings(uiRoot, "ui.root", (value, path) => {
    const scan = scanExpressionPrefixes(value)
    for (const legacy of scan.legacy) {
      issues.push({
        stage: "runtime-expr",
        level: "error",
        file: "ui.json",
        rule: "expression-prefix-legacy",
        path,
        message: `Legacy expression prefix "${legacy}." in UI binding: ${LEGACY_EXPR_PREFIXES[legacy]}.`,
      })
    }
  })
}

// ─────────────────────────────────────────────────────────────────────────────

export function runRuntimeExprStage(bundle: BlockBundle): StageResult {
  const start = Date.now()
  const issues: ValidationIssue[] = []

  const declaredPaths = getDeclaredPaths(bundle)
  const { iterateOverIds, itemVarNames } = collectIterateOverInfo(bundle.ui)

  // Validate computed variable format
  validateComputedFormat(issues, bundle)

  // Wave 9A: Detect legacy expression prefixes in UI bindings
  const uiRoot = (bundle.ui as Record<string, unknown>)?.root as Record<string, unknown> | undefined
  analyzeUiExpressionPrefixes(issues, uiRoot)

  const expressions: ExprLocation[] = []
  const filesToScan: Array<[string, unknown]> = [
    ["ui.json", bundle.ui],
    ["flow.json", bundle.flow],
    ["variables.json", bundle.variables],
    ["events.json", bundle.events],
    ["datasource.json", bundle.datasource],
  ]

  for (const [filename, data] of filesToScan) {
    extractExpressions(data, filename, "", expressions, iterateOverIds, itemVarNames)
  }

  for (const expr of expressions) {
    validateExpression(issues, expr, declaredPaths, itemVarNames)
  }

  const hasErrors = issues.some((i) => i.level === "error")
  return {
    id: "runtime-expr",
    name: "Runtime Expression Validation (path resolution, methods, iterateOver)",
    passed: !hasErrors,
    duration: Date.now() - start,
    issues,
  }
}

// ── Computed Format Validation ────────────────────────────────

/**
 * Detect computed variables with {expression: "..."} format.
 * Runtime cogo-app.tsx now supports both formats:
 *   - string: "#{expr}" (original)
 *   - object: {expression: "#{expr}"} (also supported since 2026-03-15)
 * Downgraded from error to info — both formats work at runtime.
 */
function validateComputedFormat(issues: ValidationIssue[], bundle: BlockBundle): void {
  const vars = bundle.variables as Record<string, unknown>
  const computed = vars.computed as Record<string, unknown> | undefined
  if (!computed || typeof computed !== "object") return

  for (const [key, value] of Object.entries(computed)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>
      if (typeof obj.expression === "string") {
        issues.push({
          stage: "runtime-expr",
          level: "info",
          file: "variables.json",
          rule: "computed-object-format",
          message: `Computed variable '${key}' uses {expression: "..."} format — consider using plain string format for consistency: "${key}": "${obj.expression}"`,
        })
      }
    }
  }
}

// ── Path Collection ───────────────────────────────────────────

/** Collect all declared state paths from the bundle */
function getDeclaredPaths(bundle: BlockBundle): Set<string> {
  const paths = new Set<string>()

  const vars = bundle.variables as Record<string, unknown>

  // variables.json → state keys
  if (vars.state && typeof vars.state === "object") {
    for (const key of Object.keys(vars.state as Record<string, unknown>)) {
      paths.add(key)
    }
  }
  if (vars.localState && typeof vars.localState === "object") {
    for (const key of Object.keys(vars.localState as Record<string, unknown>)) {
      paths.add(key)
    }
  }

  // variables.json → computed keys (both formats: string and {expression})
  if (vars.computed && typeof vars.computed === "object") {
    for (const key of Object.keys(vars.computed as Record<string, unknown>)) {
      paths.add(key)
    }
  }

  // Top-level keys (some blocks put state directly at root)
  for (const key of Object.keys(vars)) {
    if (key !== "state" && key !== "localState" && key !== "computed") {
      paths.add(key)
    }
  }

  // stateContract fields from block.json
  const blockData = bundle.block as Record<string, unknown>
  const stateContract = blockData.stateContract as Record<string, unknown> | undefined
  if (stateContract?.localState && typeof stateContract.localState === "object") {
    for (const key of Object.keys(stateContract.localState as Record<string, unknown>)) {
      paths.add(key)
    }
  }

  // datasource keys
  const ds = bundle.datasource as Record<string, unknown>
  if (ds.sources && typeof ds.sources === "object") {
    for (const key of Object.keys(ds.sources as Record<string, unknown>)) {
      paths.add(key)
    }
  }
  for (const key of Object.keys(ds)) {
    paths.add(key)
  }

  // Flow names (some expressions reference flow results via #{flowName})
  const flowData = bundle.flow as Record<string, unknown>
  if (flowData.flows && typeof flowData.flows === "object") {
    for (const key of Object.keys(flowData.flows as Record<string, unknown>)) {
      paths.add(key)
    }
  }

  return paths
}

// ── IterateOver Info ──────────────────────────────────────────

interface IterateOverInfo {
  iterateOverIds: Set<string>
  /** All custom itemVar names used in iterateOver directives */
  itemVarNames: Set<string>
}

function collectIterateOverInfo(ui: Record<string, unknown>): IterateOverInfo {
  const ids = new Set<string>()
  const names = new Set<string>()
  // Default item variable is always valid
  names.add("item")
  const root = ui.root as Record<string, unknown> | undefined
  if (root) gatherIterateOverInfo(root, ids, names)
  return { iterateOverIds: ids, itemVarNames: names }
}

function gatherIterateOverInfo(node: Record<string, unknown>, ids: Set<string>, names: Set<string>): void {
  if (node.iterateOver) {
    const id = node.id as string | undefined
    if (id) ids.add(id)
    // Collect custom itemVar name
    const itemVar = node.itemVar as string | undefined
    if (itemVar) names.add(itemVar)
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (child && typeof child === "object") {
        gatherIterateOverInfo(child as Record<string, unknown>, ids, names)
      }
    }
  }
  const columns = getElementField(node, "columns")
  if (Array.isArray(columns)) {
    for (const col of columns) {
      if (col && typeof col === "object") {
        const template = (col as Record<string, unknown>).template
        if (template && typeof template === "object") {
          gatherIterateOverInfo(template as Record<string, unknown>, ids, names)
        }
      }
    }
  }
}

// ── Expression Extraction ─────────────────────────────────────

/** Recursively extract #{...} and @{...} expressions from JSON values */
function extractExpressions(
  obj: unknown,
  file: string,
  path: string,
  results: ExprLocation[],
  iterateOverIds: Set<string>,
  itemVarNames: Set<string>,
  insideIterateOver = false,
): void {
  if (typeof obj === "string") {
    for (const prefix of ["#{", "@{"]) {
      let i = 0
      while (i < obj.length) {
        const pos = obj.indexOf(prefix, i)
        if (pos === -1) break
        let depth = 0
        let j = pos + 2
        let closed = false
        while (j < obj.length) {
          if (obj[j] === "{") depth++
          else if (obj[j] === "}") {
            if (depth === 0) { closed = true; break }
            depth--
          }
          j++
        }
        if (!closed) { i = j; continue }
        const body = obj.slice(pos + 2, j).trim()
        if (body) {
          results.push({ file, path, raw: obj, body, insideIterateOver })
        }
        i = j + 1
      }
    }
  } else if (obj !== null && typeof obj === "object" && !Array.isArray(obj)) {
    const record = obj as Record<string, unknown>
    // Track iterateOver context
    const hasIterateOver = !!record.iterateOver
    const newInside = insideIterateOver || hasIterateOver
    // If this node has an id in iterateOverIds, children are in iterateOver context
    const id = record.id as string | undefined
    const isIterParent = id ? iterateOverIds.has(id) : false

    for (const [key, value] of Object.entries(record)) {
      // L5 rowClassRules admission (2026-08-10, docs/superpowers/reports/
      // 2026-08-10-m7-attribution-loops/L4-r11-admission-design.md): a
      // data-table's `rowClassRules[]` entries are evaluated PER ROW by the
      // renderer (packages/cogo-react-renderer/src/renderers/
      // data-table-renderer.tsx), the same row-scope `item.*` semantics as a
      // real `iterateOver` — even though no literal `iterateOver` node
      // exists on the data-table element itself. Forced ONLY for this key's
      // own subtree (not sibling props like `columns`/`data` on the same
      // node) so an `item.*` reference elsewhere on the element that is
      // genuinely NOT row-scoped (e.g. a column's `visibleWhen`, evaluated
      // once, not per row) still correctly trips
      // `expr-item-without-iterate`.
      const forcedInsideForRowClassRules = key === "rowClassRules"
      // H1 (2026-08-20) declared the per-column channel: renderer
      // `case "custom"` mounts `col.template` under
      // `buildIteratedResolveSymbol(item, "item", …)`. The prompt and
      // element-types.json `readSite` already claim this stage honours
      // that row scope; only `rowClassRules` was forced, so a generated
      // `#{item.*}` inside `columns[].template` still tripped
      // `expr-item-without-iterate`. Force that subtree when the column
      // entry itself is `type: "custom"` — the same discriminant the
      // renderer reads. `visibleWhen` on the same column stays unforced.
      const forcedInsideForCustomColumnTemplate =
        key === "template" && record.type === "custom"
      extractExpressions(
        value, file, path ? `${path}.${key}` : key, results, iterateOverIds, itemVarNames,
        newInside || isIterParent || forcedInsideForRowClassRules || forcedInsideForCustomColumnTemplate,
      )
    }
  } else if (Array.isArray(obj)) {
    for (let idx = 0; idx < obj.length; idx++) {
      extractExpressions(
        obj[idx], file, `${path}[${idx}]`, results, iterateOverIds, itemVarNames,
        insideIterateOver,
      )
    }
  }
}

// ── Expression Validation ─────────────────────────────────────

/** Validate a single expression */
function validateExpression(
  issues: ValidationIssue[],
  expr: ExprLocation,
  declaredPaths: Set<string>,
  itemVarNames: Set<string>,
): void {
  const { body } = expr

  // Skip pure literals (numbers, quoted strings, booleans)
  if (/^-?\d+(\.\d+)?$/.test(body)) return
  if (/^["'].*["']$/.test(body)) return
  if (body === "true" || body === "false" || body === "null") return

  // Check for method calls
  validateMethods(issues, expr)

  // Find lambda / object-form: nested accessors are silent-empty at runtime
  validateFindPredicate(issues, expr)

  // Check for item.* outside iterateOver (skip custom itemVars)
  validateItemContext(issues, expr, itemVarNames)

  // Extract state paths and validate they exist
  validatePaths(issues, expr, declaredPaths, itemVarNames)

  // Check call-position identifiers against the declared global-function surface
  validateGlobalFunctionCalls(issues, expr)

  // Check arithmetic expressions for potentially non-numeric paths
  validateArithmetic(issues, expr, declaredPaths, itemVarNames)

  // Check for #{localState.X} — should be #{local.X}
  validateLocalStatePath(issues, expr)

  // GV-8: Validate reduce pattern
  validateReducePattern(issues, expr)

  // GV-8: Validate ternary pattern
  validateTernaryPattern(issues, expr)
}

/**
 * Check that method calls use valid method names.
 *
 * Severity is `error` (promoted from `warning` — see FINDINGEXP-1,
 * 2026-08-07). This stage is registered `safety: true` in
 * `safety-core.ts#ALL_STAGES` (a non-filterable hard-block stage per D-406 /
 * Phase 472), and `runStages`/`runSafetyContract`'s per-stage `passed` field
 * is `!issues.some(i => i.level === "error")` — a `warning`-level finding
 * can NEVER fail a safety stage, no matter how confidently it is detected.
 * A rule that both (a) lives inside a stage declared non-filterable-safety
 * and (b) is capped below the severity that stage's own pass/fail gate reads
 * is structurally incapable of blocking anything it finds — decorative
 * detection, not enforcement. The evaluator has no handler for a
 * non-canonical accessor (e.g. `findIndex` — only `find` is canonical; see
 * `expression-accessors-v1.ttl` / `compute-evaluator.ts`), so every
 * expression this rule flags resolves to `undefined` at runtime — that is a
 * genuine execution-boundary break, exactly what `safety: true` is defined
 * (safety-core.ts:79-80) to reject, not merely score as suboptimal.
 */

/**
 * `evalFilterCondition` path-resolves each side of `===` (compute-evaluator.ts).
 * Nested `.replace(`/`.substring(` inside `.find(...)` therefore matches nothing
 * and does not throw. Derived from that consumer, not from the M7 ruler.
 */
function validateFindPredicate(issues: ValidationIssue[], expr: ExprLocation): void {
  const body = expr.body
  let searchFrom = 0
  while (true) {
    const start = body.indexOf(".find(", searchFrom)
    if (start < 0) break
    const open = start + 6
    let depth = 1
    let i = open
    while (i < body.length && depth > 0) {
      if (body[i] === "(") depth++
      else if (body[i] === ")") depth--
      i++
    }
    if (depth === 0) {
      const findBody = body.slice(open, i - 1)
      if (/\.(replace|substring|substr|slice|split)\s*\(/.test(findBody)) {
        issues.push({
          stage: "runtime-expr",
          level: "error",
          file: expr.file,
          rule: "expr-find-predicate-nested-accessor",
          message:
            "Expression '#{" + body + "}' at " + expr.path +
            " nests a transform accessor inside .find(...). " +
            "The runtime find/filter evaluator resolves each === side as a property path, so the match is silently empty. " +
            "Compute the cleaned field first, then arr.find({key: value}).",
        })
      }
    }
    searchFrom = start + 1
  }
}

function validateMethods(issues: ValidationIssue[], expr: ExprLocation): void {
  const methodPattern = /\.([a-zA-Z_]\w*)\s*\(/g
  let match: RegExpExecArray | null
  while ((match = methodPattern.exec(expr.body)) !== null) {
    const methodName = match[1]
    if (!VALID_EXPRESSION_METHODS.has(methodName)) {
      issues.push({
        stage: "runtime-expr",
        level: "error",
        file: expr.file,
        rule: "expr-invalid-method",
        message: `Expression '#{${expr.body}}' at ${expr.path} uses unknown method '${methodName}()' — not in the canonical accessor set (expression-accessors-v1.ttl); it will evaluate to undefined at runtime.`,
      })
    }
  }
}

/** Check #{item.*} usage outside iterateOver context */
function validateItemContext(issues: ValidationIssue[], expr: ExprLocation, itemVarNames: Set<string>): void {
  if (expr.insideIterateOver) return
  // Only flag the default "item" — custom itemVars (cartItem, etc.) are tracked separately
  if (/\bitem\./.test(expr.body)) {
    // Skip if "item" appears inside a callback: .reduce((sum, item) => ...)
    if (isInsideCallback(expr.body, "item")) return

    issues.push({
      stage: "runtime-expr",
      level: "info",
      file: expr.file,
      rule: "expr-item-without-iterate",
      message: `Expression '#{${expr.body}}' at ${expr.path} references 'item.*' but is not inside an iterateOver element`,
    })
  }
}

/** Check if a variable name appears as a callback parameter (e.g., in .reduce((sum, item) => ...)) */
function isInsideCallback(body: string, varName: string): boolean {
  ARROW_CALLBACK_PATTERN.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = ARROW_CALLBACK_PATTERN.exec(body)) !== null) {
    const params = m[1] ?? m[2] ?? ""
    const paramNames = params.split(",").map((s) => s.trim())
    if (paramNames.includes(varName)) return true
  }
  return false
}

/** Collect callback parameter names from arrow functions in expression */
function collectCallbackParams(body: string): Set<string> {
  const params = new Set<string>()
  ARROW_CALLBACK_PATTERN.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = ARROW_CALLBACK_PATTERN.exec(body)) !== null) {
    const raw = m[1] ?? m[2] ?? ""
    for (const p of raw.split(",")) {
      const name = p.trim()
      if (name) params.add(name)
    }
  }
  return params
}

/** Validate that referenced paths exist in declared state */
function validatePaths(
  issues: ValidationIssue[],
  expr: ExprLocation,
  declaredPaths: Set<string>,
  itemVarNames: Set<string>,
): void {
  const paths = extractPathsFromExpr(expr.body)
  const callbackParams = collectCallbackParams(expr.body)
  const callees = extractCalleesFromExpr(expr.body)

  for (const p of paths) {
    const rootKey = p.split(".")[0]
    // Skip runtime namespaces
    if (RUNTIME_NAMESPACES.has(rootKey)) continue
    // Skip if declared
    if (declaredPaths.has(rootKey)) continue
    if (declaredPaths.has(p)) continue
    // Skip numeric-looking segments (array indices, literal numbers)
    if (/^\d+$/.test(rootKey)) continue
    // Skip custom itemVar names (e.g., "cartItem" from iterateOver)
    if (itemVarNames.has(rootKey)) continue
    // Skip arrow callback parameters (e.g., "sum" in .reduce((sum, item) => ...))
    if (callbackParams.has(rootKey)) continue
    // Skip call-position identifiers — a callee is not a state path, declared
    // or otherwise. `extractPathsFromExpr` splits on `(`/`)`, so `concat(...)`
    // yields the bare token `concat`; that token is validated separately by
    // `validateGlobalFunctionCalls`, not reported here.
    if (callees.has(rootKey)) continue

    issues.push({
      stage: "runtime-expr",
      level: "info",
      file: expr.file,
      rule: "expr-undeclared-path",
      message: `Expression '#{${expr.body}}' at ${expr.path} references undeclared path '${p}'`,
    })
  }
}

/**
 * Check that call-position identifiers are declared global functions.
 *
 * This is the deliberate check the accidental `expr-undeclared-path` false
 * positive was standing in for: suppressing that false positive alone would
 * delete the only signal a typo'd function name currently produces (there is
 * no other global-function-name validation in this package —
 * `expr-invalid-method` covers only `.method()`). Severity is `warning`, not
 * `error`: this stage is `safety: true` (non-filterable) per D-406/Phase 472,
 * and an unrecognised bare callee is advisory the same way `expr-invalid-
 * method`'s notional counterpart would be — it does not itself prove the
 * expression breaks at runtime the way an invalid `.method()` does.
 *
 * `RUNTIME_NAMESPACES` names are exempt too, not just `DECLARED_GLOBAL_
 * FUNCTIONS`: the corpus probe that caught the string-literal bug above also
 * found `new Date().toISOString()` and `Number(ui.sellingPrice)` in real,
 * presumably-intentional fixtures. `Date`/`Number` are `EVALUATOR_GLOBALS`
 * (`expression-namespaces.ts`) — JS-native constructor/namespace objects the
 * evaluator already exposes and that module already declares legal in this
 * position ("a reference rooted at one of them is legal and must not be
 * reported as an undeclared path") — distinct from the TTL's GlobalFn/
 * DollarFn category, which is the CUSTOM Cogo-authored function surface.
 * Without this exemption the rule would warn on every such call, which is
 * exactly the kind of false positive this task exists to stop producing, not
 * reintroduce under a new name.
 *
 * `CALL_LIKE_JS_KEYWORDS` (`typeof`, `function`) are exempt for the same
 * reason — `typeof(x)` and an IIFE's `function(){...}()` head are call-shaped
 * but are JS grammar, not callees. Fix-round-1 Minor.
 */
function validateGlobalFunctionCalls(issues: ValidationIssue[], expr: ExprLocation): void {
  for (const callee of extractCalleesFromExpr(expr.body)) {
    if (DECLARED_GLOBAL_FUNCTIONS.has(callee)) continue
    if (RUNTIME_NAMESPACES.has(callee)) continue
    if (CALL_LIKE_JS_KEYWORDS.has(callee)) continue
    issues.push({
      stage: "runtime-expr",
      level: "warning",
      file: expr.file,
      rule: "expr-unknown-global-fn",
      message: `Expression '#{${expr.body}}' at ${expr.path} calls '${callee}', which is not a declared global function`,
    })
  }
}

/** Warn about arithmetic on potentially non-numeric paths */
function validateArithmetic(
  issues: ValidationIssue[],
  expr: ExprLocation,
  declaredPaths: Set<string>,
  itemVarNames: Set<string>,
): void {
  if (!/[+\-*/%]/.test(expr.body)) return
  if (/["']/.test(expr.body)) return

  const paths = extractPathsFromExpr(expr.body)
  const callbackParams = collectCallbackParams(expr.body)

  for (const p of paths) {
    const rootKey = p.split(".")[0]
    if (RUNTIME_NAMESPACES.has(rootKey)) continue
    if (/^\d+$/.test(rootKey)) continue
    if (itemVarNames.has(rootKey)) continue
    if (callbackParams.has(rootKey)) continue

    if (declaredPaths.has(rootKey)) {
      if (/(?:name|label|title|text|description|key|type|status)$/i.test(rootKey)) {
        issues.push({
          stage: "runtime-expr",
          level: "info",
          file: expr.file,
          rule: "expr-arithmetic-path",
          message: `Expression '#{${expr.body}}' at ${expr.path} uses arithmetic on path '${p}' which may be non-numeric`,
        })
      }
    }
  }
}

// ── localState → local path validation ───────────────────────

/**
 * Detect #{localState.X} in expressions — runtime uses #{local.X}.
 * The JSON schema field is stateContract.localState (correct), but
 * expression paths must use the `local` namespace at runtime.
 */
function validateLocalStatePath(issues: ValidationIssue[], expr: ExprLocation): void {
  if (!/\blocalState\./.test(expr.body)) return

  // Extract all localState.xxx references
  const matches = expr.body.matchAll(/\blocalState\.(\w+)/g)
  for (const m of matches) {
    const varName = m[1]
    issues.push({
      stage: "runtime-expr",
      level: "warning",
      file: expr.file,
      rule: "expr-localstate-path",
      message: `Expression '#{${expr.body}}' at ${expr.path} uses 'localState.${varName}' — runtime resolves local state as 'local.${varName}'. Use #{local.${varName}} instead.`,
    })
  }
}

// ── GV-8: Reduce Pattern Validation ──────────────────────────

/** Validate reduce expressions have proper accumulator/callback pattern */
function validateReducePattern(issues: ValidationIssue[], expr: ExprLocation): void {
  if (!expr.body.includes(".reduce(")) return

  // Extract reduce callback: .reduce(callback, initialValue)
  const reduceMatch = expr.body.match(/\.reduce\(\s*\(([^)]*)\)\s*=>/)
  if (!reduceMatch) {
    // Check for reduce without arrow function
    if (/\.reduce\(\s*[^(]/.test(expr.body)) {
      issues.push({
        stage: "runtime-expr",
        level: "warning",
        file: expr.file,
        rule: "expr-reduce-valid",
        message: `Expression '#{${expr.body}}' at ${expr.path} has .reduce() without arrow function callback. Use .reduce((acc, item) => acc + item.field, 0) pattern.`,
      })
    }
    return
  }

  const params = reduceMatch[1].split(",").map(s => s.trim())

  // Reduce must have exactly 2 callback params: (accumulator, currentItem)
  if (params.length < 2) {
    issues.push({
      stage: "runtime-expr",
      level: "warning",
      file: expr.file,
      rule: "expr-reduce-valid",
      message: `Expression '#{${expr.body}}' at ${expr.path} has .reduce() with ${params.length} callback param(s), expected 2 (accumulator, item).`,
    })
    return
  }

  // Check for initial value (the second arg to reduce)
  // Pattern: .reduce((a, b) => ..., INIT)
  // We look for a comma after the arrow function body followed by a value
  const afterReduce = expr.body.slice(expr.body.indexOf(".reduce(") + 8)
  // Count balanced parens to find the closing of reduce
  let depth = 1
  let i = 0
  while (i < afterReduce.length && depth > 0) {
    if (afterReduce[i] === "(") depth++
    else if (afterReduce[i] === ")") depth--
    i++
  }
  const reduceContent = afterReduce.slice(0, i - 1)
  // Check for initial value: should have a comma after the arrow function
  const hasInitialValue = /,\s*(-?\d+(\.\d+)?|true|false|null|\[\]|\{\}|"[^"]*")\s*$/.test(reduceContent)

  if (!hasInitialValue) {
    issues.push({
      stage: "runtime-expr",
      level: "info",
      file: expr.file,
      rule: "expr-reduce-valid",
      message: `Expression '#{${expr.body}}' at ${expr.path} has .reduce() without explicit initial value. Consider adding initial value (e.g., 0) as second argument.`,
    })
  }
}

// ── GV-8: Ternary Pattern Validation ─────────────────────────

/** Validate ternary expressions have proper comparison and quote matching */
function validateTernaryPattern(issues: ValidationIssue[], expr: ExprLocation): void {
  if (!expr.body.includes("?") || !expr.body.includes(":")) return

  // Check for = instead of === or == in comparison
  const singleEqualMatch = expr.body.match(/[^=!<>]=[^=]/)
  if (singleEqualMatch) {
    // Exclude arrow functions (=>), key-value pairs, and assignments in reduce
    const before = expr.body.slice(0, singleEqualMatch.index! + 1)
    if (!before.endsWith(">") && !before.endsWith("!") && !before.endsWith("<")) {
      // Skip if inside arrow function or object literal
      if (!/=>/.test(expr.body.slice(Math.max(0, singleEqualMatch.index! - 5), singleEqualMatch.index! + 5))) {
        issues.push({
          stage: "runtime-expr",
          level: "info",
          file: expr.file,
          rule: "expr-ternary-valid",
          message: `Expression '#{${expr.body}}' at ${expr.path} uses single '=' in comparison. Use '===' for strict equality.`,
        })
      }
    }
  }

  // Check for unmatched quotes in ternary branches
  const quotes = expr.body.match(/'/g)
  if (quotes && quotes.length % 2 !== 0) {
    issues.push({
      stage: "runtime-expr",
      level: "warning",
      file: expr.file,
      rule: "expr-ternary-valid",
      message: `Expression '#{${expr.body}}' at ${expr.path} has unmatched single quotes in ternary expression.`,
    })
  }
}

/** Extract state path references from an expression body, ignoring operators and literals */
function extractPathsFromExpr(body: string): string[] {
  const paths: string[] = []
  // Remove string literals
  let cleaned = body.replace(/["'][^"']*["']/g, "")
  // Remove ternary ? : operators but keep content
  cleaned = cleaned.replace(/\?/g, " ").replace(/:/g, " ")
  // Remove arrow function markers
  cleaned = cleaned.replace(/=>/g, " ")
  // Split by operators and whitespace
  const tokens = cleaned.split(/[\s+\-*/%><=!&|,()]+/)
  for (const token of tokens) {
    const t = token.trim()
    if (!t) continue
    // Skip pure numbers
    if (/^-?\d+(\.\d+)?$/.test(t)) continue
    // Skip keywords
    if (["true", "false", "null", "undefined", "NaN", "Infinity"].includes(t)) continue
    // Must start with a letter or underscore (valid identifier)
    if (/^[a-zA-Z_]/.test(t)) {
      paths.push(t)
    }
  }
  return paths
}
